import { useCallback, useContext, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components';
import { IPageState, PageContext } from '../../contexts/PageContext';
import { CHARGE_TYPES, CURRENCIES, PATHS, PURITY_TESTING_STEPS, TRANSACTION_STATUS, TRANSACTION_TYPES, WEIGHT_UNITS } from '../../utils/constant';
import Inbound from '../$Inbound';
import { formatDate, getFormattedCurrency, getThreePointNumber, getTwoPointNumber, objToArray } from '../../utils/helpers';
import Summary from '../$Summary';
import Billing from '../$Billing';
import SelectCustomer from '../$SelectCustomer';
import { TransactionContext } from '../../contexts/TransactionContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TTransactionBasic } from '../../Models/Transactions';
import useService from '../../hooks/useService';
import { fetchCharges, getLotDetails, getTransaction as getTransactionService } from '../../services/Transactions';
import { CustomerContext } from '../../contexts/CustomerContext';
import { TSummaryField } from '../../Models/Summary';
import HighlightsCardAndCustomerDetails from '../custom/HighlightsCardAndCustomerDetails';


export type TPurityTestingSectionKeys = 'INBOUND' | 'BILLING' | 'SUMMARY' 

const PAGE_INFO: IPageState = {
    title: 'Purity Testing',
    breadcrumbs: objToArray(PURITY_TESTING_STEPS).map(item => ({path: item.key, label: item.value})),
    currentPage: PURITY_TESTING_STEPS.INBOUND,
    path: PATHS.PURITY_TESTING
}


const Container = styled.div`
    width: 100%;
`

type TPurityTestingSummaryKeys = 
'customerId' 
| 'customerName' 
| 'pvcName' 
| 'transactionDate' 
| 'purityTestingCharges' 
| 'totalWBMGold'
| 'totalWAMGold'
| 'totalWAMTGold'
| 'settlementFineWeightGold'
| 'totalTaxValue'
| 'totalCharges'
| 'roundingRs'
| 'totalValue'
| 'totalMetalReturnNetWeightGold'

const SUMMARY_DETAILS: {id: TPurityTestingSummaryKeys, label: any, value: any}[] = [
    {
        id: 'customerId',
        label:"Customer Id",
        value : ""
    },
    {
        id:"customerName",
        label:"Customer Name",
        value : ""
    },
    {
        id:"pvcName",
        label:"PVC Name",
        value : ""
    },
    {
        id:"transactionDate",
        label:"Transaction Date",
        value : ""
    },
    {
        id:"purityTestingCharges",
        label:"Purity Testing Charges",
        value : ""
    },
    {
        id:'totalWBMGold',
        label:"Total WBM",
        value : ''
    },
    {
        id:'totalWAMGold',
        label:"Total WAM",
        value : ''
    },
    {
        id:'totalWAMTGold',
        label:"Total WAMT",
        value : ''
    },
    {
        id:'settlementFineWeightGold',
        label:"Settlement Fine Wt. Gold",
        value : ''
    },
    {
        id:'totalMetalReturnNetWeightGold',
        label:"Total Metal Return Net Wt. Gold",
        value : ''
    },
    {
        id:'totalTaxValue',
        label:"Total Tax Value Rs.",
        value : ''
    },
    {
        id:'totalCharges',
        label:"Total Charges Rs.",
        value : ''
    },
    {
        id:'roundingRs',
        label:"Rounding Rs.",
        value : ''
    },
    {
        id:'totalValue',
        label:"Grand Total Rs.",
        value : ''
    },

]

const getPageInfo = (section: TPurityTestingSectionKeys) => {
    const pageInfo = {...PAGE_INFO, currentPage:section || 'INBOUND'}
    return pageInfo
}


const PurityTesting = () => {

    const { setPageState } = useContext(PageContext)
    const [searchParams] = useSearchParams()

    const { physicalOrder, meltingLot, setTransactionInfo, transactionInfo: { transactionType, globalTransactionNumber, status,updatedAt }, pvcCenterDetails, inboundDetails, resetOrder, setCapturedSteps } = useContext(TransactionContext)
    const { user } = useContext(AuthContext)
    const { customerConfig } = useContext(CustomerContext)
    const { id } = useParams()
    const { execute } = useService()
    const navigate = useNavigate()
    const step = searchParams.get('section')

    useEffect(() => {
        setPageState(getPageInfo(step as TPurityTestingSectionKeys))
    }, [step])

    const getTransaction = async () => {
        const { data } = await execute(() => getTransactionService({ globalTransactionNumber: id!, transactionType }))
        if(data?.status === TRANSACTION_STATUS.COMPLETED) await execute(()=> getLotDetails({globalTransactionNumber:data?.globalTransactionNumber!}))
        if (data !== null) {
            switch (data.status) {
                case TRANSACTION_STATUS.INBOUND_INITIATED:
                    navigate(PATHS.PURITY_TESTING + '/' + id + '?section=INBOUND')
                    break;
                case TRANSACTION_STATUS.PAYMENT_INITIATED:
                    setCapturedSteps(['INBOUND'])
                    navigate(PATHS.PURITY_TESTING + '/' + id + '?section=BILLING')
                    break;
                case TRANSACTION_STATUS.COMPLETED:
                case TRANSACTION_STATUS.PENDING_CANCELLATION:
                case TRANSACTION_STATUS.CANCELLED:
                    setCapturedSteps(['INBOUND', 'BILLING'])
                    navigate(PATHS.PURITY_TESTING + '/' + id + '?section=SUMMARY')
                    break;
            }
        }
    }

    useEffect(() => {
        resetOrder()

        if (id) {
            getTransaction()
        }
        else {

            setTransactionInfo({
                currency: CURRENCIES.INR,
                pvcCenterId: user.centerId!,
                status: TRANSACTION_STATUS.INBOUND_INITIATED,
                transactionType: TRANSACTION_TYPES.PURITY_TESTING,
                weightUOM: WEIGHT_UNITS.GM,
            } as TTransactionBasic)
        }

    }, [])

    const getHightlights = () => {
        const highlights = [
            { label: 'Transaction Number', value: globalTransactionNumber },
            { label: 'Status', value: status },
            { label: 'Customer', value: customerConfig.displayName || customerConfig.fullName || '' },
        ]

        if (customerConfig.gstin) highlights.push({ label: 'GSTIN', value: customerConfig.gstin })
        if (customerConfig.pan) highlights.push({ label: 'PAN', value: customerConfig.pan })
        if (inboundDetails.id) highlights.push({ label: 'Inbound Header', value: inboundDetails.id })

        return highlights
    }

    const getCharges = () => {
        execute(()=>fetchCharges({globalTransactionNumber: globalTransactionNumber!, transactionType:transactionType}))
    }

    const getSummary = () => {
        let totalWBM = 0
        let totalWAM = 0
        let totalWAMT = 0
        let settlementFineWeightGold = 0
        let totalMetalReturnNetWeightGold = 0
        console.log('in summary...', meltingLot)
        meltingLot.forEach(lot => {
            console.log('lot in summary...')
            totalWBM += lot.netWeight
            totalWAM += lot.weightAfterMelting
            totalWAMT += lot.wamt
            settlementFineWeightGold += lot.fineWeightToReturn
            totalMetalReturnNetWeightGold += lot.finalSettlementNetWeight
        })
        let totalTaxValue = 0
        physicalOrder.taxes?.forEach((tax) => {
            totalTaxValue += tax.taxAmount
        })
        let totalCharges = 0
        physicalOrder.charges?.forEach((charge) => {
            totalCharges += charge.preTaxAmount
            totalTaxValue += charge.postTaxAmount - charge.preTaxAmount
        })
        const roundingRs = getTwoPointNumber((Math.round(physicalOrder.totalPrice || 0) - (physicalOrder.totalPrice || 0)) ?? 0)
        const purityTestingCharges = physicalOrder.charges?.filter(charge => charge.chargeId === CHARGE_TYPES.PURITY_TESTING_CHARGE)[0]
        return SUMMARY_DETAILS.map(field => {
            switch (field.id) {
                case 'customerId':
                    field.value = customerConfig.customerRefNo
                    break;
                case 'customerName':
                    field.value = customerConfig.fullName
                    break;
                case 'pvcName':
                    field.value = pvcCenterDetails.name
                    break;
                case 'transactionDate':
                    field.value = updatedAt ? formatDate(updatedAt) : ''
                    break;
                case 'purityTestingCharges':
                    field.value = purityTestingCharges ? getFormattedCurrency(purityTestingCharges.preTaxAmount) : ''
                    break;
                case 'totalWBMGold':
                    field.value = getThreePointNumber(totalWBM)
                    break;
                case 'totalWAMGold':
                    field.value = getThreePointNumber(totalWAM)
                    break;
                case 'totalWAMTGold':
                    field.value = getThreePointNumber(totalWAMT)
                    break;
                case 'settlementFineWeightGold':
                    field.value = getThreePointNumber(settlementFineWeightGold)
                    break;
                case 'totalMetalReturnNetWeightGold':
                    field.value = getThreePointNumber(totalMetalReturnNetWeightGold)
                    break;
                case 'totalTaxValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalTaxValue))
                    break;
                case 'totalCharges':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalCharges))
                    break;
                case 'roundingRs':
                    field.value = getFormattedCurrency(getTwoPointNumber(roundingRs))
                    break;
                case 'totalValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(physicalOrder.totalPrice || 0))
                    break;
                    
            }
            return field
        })
    }

    const cancelTransactionHandler = (transactionId:string) => {
        navigate(PATHS.PVC_TRANSACTIONS)
    }
    
    useEffect(() => {
        if(globalTransactionNumber) getCharges()
    }, [globalTransactionNumber])

    const PageContent = useCallback(() => {
        switch (step as TPurityTestingSectionKeys) {
            case 'INBOUND':
                return <Inbound nextButtonLable='Next (Billing)' nextSection='BILLING' path={PATHS.PURITY_TESTING} />

            case 'BILLING':
                return <Billing nextButtonLable='Generate Invoice' nextSection='SUMMARY' prevSection='INBOUND' path={PATHS.PURITY_TESTING} />

            case 'SUMMARY':
                return <Summary prevSection='BILLING' transitionHistory={[]} summaryDetails={getSummary()} path={PATHS.PURITY_TESTING} />

            default:
                return <SelectCustomer path={PATHS.PURITY_TESTING} nextSection='INBOUND' />
        }
    }, [step, inboundDetails])

    return (
        <Container>
            { id ? <HighlightsCardAndCustomerDetails
                        customerConfig={customerConfig}
                        globalTransactionNumber={globalTransactionNumber as string}
                        status={status}
                        inboundId={inboundDetails.id}
                        transactionType={transactionType}
                        onSuccessCancelTransaction={cancelTransactionHandler}
                    /> 
                : <></>
             }
            <PageContent />
        </Container>
    )
}


export default PurityTesting